import React, { useEffect, useState } from 'react'
import {
  getPaymentsTotal,
  hasPayments,
  hasSurchargeRefundable,
} from '../../../functions/sale'
import { useRegisterManager, useSaleManager } from '../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  selectCurrentSale,
  selectPromotionSummary,
  selectSaleNote,
  updateDisplayDiscount,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { SaleEcomCustomCharges } from '../sale-ecom-custom-charges/SaleEcomCustomCharges'
import { SaleTaxBreakdown } from '../sale-tax-breakdown/SaleTaxBreakdown'
import { AddRow } from './add-row'
import { DiscountRow } from './discount-row'
import { Notes } from './notes'
import { PromotionRow } from './Promotion/PromotionRow'
import { PaymentsRow } from './sale-payments'
import { ServiceFee } from './service-fee'
import { Subtotal } from './subtotal'
import { Total } from './total'

export const SaleFooter: React.FC = () => {
  const [displayDiscountRow, setDisplayDiscountRow] = useState(false)
  const [showDiscountPopover, setShowDiscountPopover] = useState(false)
  const dispatch = useAppDispatch()
  const saleNote = useAppSelector(selectSaleNote)
  const hasSaleNote = saleNote.length > 0
  const [displaySaleNoteRow, setDisplaySaleNoteRow] = useState(false)
  const [focusOnSaleNote, setFocusOnSaleNote] = useState<boolean>(false)
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()
  const sale = useAppSelector(selectCurrentSale)
  const promotionSummary = useAppSelector(selectPromotionSummary)

  // When we load a parked sale, or we create a new sale, we want to hide the discount row.
  // Note we can't make this depend on saleNote.length, because doing so would cause the row to be hidden when the note content is deleted.
  useEffect(() => {
    setDisplaySaleNoteRow(false)
  }, [sale.localId])

  // When we load a parked sale with a note, we want to show the note row.
  // We need to do this so that the note input is visible, even if the note content is empty.
  useEffect(() => {
    if (hasSaleNote) {
      setDisplaySaleNoteRow(true)
    }
  }, [hasSaleNote])

  useEffect(() => {
    saleManager.on('EventEmitter:sale:new', () => {
      setDisplayDiscountRow(false)
    })
    return () => {
      saleManager.off('EventEmitter:sale:new')
    }
  }, [saleManager])

  const removeSaleDiscount = () => {
    setDisplayDiscountRow(false)
    dispatch(
      updateDisplayDiscount({
        displayDiscount: '0',
        discountIsPercentage: sale.discountIsPercentage,
      })
    )
    saleManager.updateSaleDiscount()
  }

  const openDiscountPopover = () => setShowDiscountPopover(true)

  const closeDiscountPopover = () => {
    if (!sale.discount || sale.discount.price === 0) {
      setDisplayDiscountRow(false)
    }
    setShowDiscountPopover(false)
  }

  const displayAddRow =
    !sale.discount || !displaySaleNoteRow || !saleManager.hasAppliedPromoCode()
  const displaySubtotal = sale.finals && !registerManager.isTaxInclusive()
  const paymentsTotal = getPaymentsTotal(sale.payments)
  const displayTotal = paymentsTotal !== 0

  const handleNoteLinkClick = () => {
    setDisplaySaleNoteRow(true)
    setFocusOnSaleNote(true)
  }

  const displayPromotionRow = promotionSummary.length > 0
  const displaySurcharge = sale.finals && hasSurchargeRefundable(sale)
  const displayTaxRow = sale.finals && sale.finals.taxDisplayProperties?.taxName
  const displayPaymentsRow = hasPayments(sale)
  const displayEcomCustomSurcharges = saleManager.hasEcomCustomSurcharges()

  return (
    <div data-testid="sale_footer" className="sale-list-footer">
      <ul className="sale-totals">
        {displayAddRow && (
          <li className="sale-total-line sale-add-line">
            <AddRow
              openDiscountPopover={openDiscountPopover}
              setDisplaySaleDiscountRow={setDisplayDiscountRow}
              displayDiscountRow={displayDiscountRow}
              showSaleNoteLink={!displaySaleNoteRow}
              onNoteLinkClick={handleNoteLinkClick}
            />
          </li>
        )}

        {displaySaleNoteRow && (
          <li className="sale-add-line sale-note-line">
            <Notes
              shouldFocus={focusOnSaleNote}
              onDeleted={() => setDisplaySaleNoteRow(false)}
            />
          </li>
        )}

        {displaySubtotal && (
          <li
            className="sale-total-line sale-add-line vd-text-label"
            data-cy="sale-subtotal"
          >
            <Subtotal subtotal={sale.finals.subtotal} />
          </li>
        )}

        {(displayDiscountRow ||
          sale.discount ||
          sale.displayDiscount !== '0') && (
          <li className="sale-total-line sale-add-line" data-cy="sale-discount">
            <DiscountRow
              showDiscountPopover={showDiscountPopover}
              removeSaleDiscount={removeSaleDiscount}
              closePopover={closeDiscountPopover}
              openDiscountPopover={openDiscountPopover}
            />
          </li>
        )}

        {displayPromotionRow && (
          <li
            className="sale-total-line sale-add-line"
            data-cy="sale-promotion-data"
          >
            <PromotionRow promotionSummary={promotionSummary} />
          </li>
        )}

        {displayTaxRow && (
          <li className="sale-total-line sale-add-line" data-cy="sale-tax">
            <SaleTaxBreakdown />
          </li>
        )}

        {(displayEcomCustomSurcharges || displaySurcharge) && (
          <li className="sale-total-line sale-add-line">
            <SaleEcomCustomCharges />
          </li>
        )}

        {saleManager.hasCashDiscountingServiceFee() && (
          <li className="sale-total-line sale-add-line">
            <ServiceFee />
          </li>
        )}

        {displayTotal && (
          <li className="vd-text-label sale-total-line sale-add-line pro-sale-total-line-total">
            <Total total={sale.finals.inclusivePrice} />
          </li>
        )}

        {displayPaymentsRow && (
          <li
            className="sale-total-line sale-add-line pro-total-line-payments"
            data-cy="sale-payments"
          >
            <PaymentsRow payments={sale.payments} />
          </li>
        )}
      </ul>
    </div>
  )
}
